import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@tempmee/react';
import { OutlinePlusIcon } from '@tempmee/react-icons';
import { useOfficeDiscountHistory } from '../../../../domain/Campaigns';
import { ProfileTabBody } from '../components/ProfileTabBody';
import { ApplyPromoCodeDialog } from './ApplyPromoCodeDialog';
import { OfficePromoCodesTable } from './OfficePromoCodesTable';
import { RemovePromoCodeDialog } from './RemovePromoCodeDialog';

/**
 * The office profile's Promotions → Discounts & Promo Codes tab (GOTU-6663).
 *
 * Office-only — it is registered on DNTTabs, not HYGTabs. The office id comes
 * from the profile's Redux `currentUser`; until that has resolved the query is
 * disabled and the card shows its loading state, since an empty list there
 * would claim the office has never been on a promotion.
 *
 * Since GOTU-9821 an admin can also apply a code on the office's behalf and
 * take an applied one back. Both dialogs refresh the history on success, so
 * the table only ever shows what campaign-service recorded. Apply stays
 * disabled until the office id is known — the route is keyed on it.
 *
 * Only applied codes are listed (GOTU-19330). The history also carries every
 * live campaign the office merely matches, and a code an admin removed; both
 * stay hidden. campaign-service has no applied-only filter, so it is done here.
 */
export const OfficePromoCodesTabScreen = () => {
  const officeId = useSelector((state) => state.users.currentUser?.id);
  const { history, isPending, isError } = useOfficeDiscountHistory(officeId);
  const appliedCodes = history.filter((row) => row.applied);
  const [isApplyOpen, setIsApplyOpen] = useState(false);
  const [rowToRemove, setRowToRemove] = useState(null);

  const applyAction = (
    <Button
      variant="outlined"
      color="secondary"
      size="small"
      disabled={!officeId}
      onClick={() => setIsApplyOpen(true)}
      startIcon={<OutlinePlusIcon aria-hidden className="size-5" />}
    >
      Apply promo code
    </Button>
  );

  return (
    <ProfileTabBody>
      <OfficePromoCodesTable
        history={appliedCodes}
        isPending={isPending || !officeId}
        isError={isError}
        action={applyAction}
        onRemove={setRowToRemove}
      />

      {isApplyOpen && (
        <ApplyPromoCodeDialog officeId={officeId} onClose={() => setIsApplyOpen(false)} />
      )}

      {rowToRemove && (
        <RemovePromoCodeDialog
          officeId={officeId}
          row={rowToRemove}
          onClose={() => setRowToRemove(null)}
        />
      )}
    </ProfileTabBody>
  );
};

export default OfficePromoCodesTabScreen;
